import clsx from "clsx";
import {
  formatHours,
  getHoursUsageTrafficLight,
  hoursUsageStatus,
  toHoursUsageDisplay,
  type HoursUsageEntry,
} from "@/pages/projekte/hoursUsage";

export function HoursUsageOverview({
  entry,
  workshopHours,
  montageHours,
}: {
  entry: HoursUsageEntry;
  workshopHours: number | null;
  montageHours: number | null;
}) {
  if (!entry.eligible) return null;

  const planned = entry.plannedHours;
  const actual = entry.actualHours;
  const percentRaw = entry.percentRaw;
  const display = toHoursUsageDisplay(entry);
  const traffic = getHoursUsageTrafficLight(percentRaw);
  const showMeter =
    !entry.loading &&
    !entry.error &&
    planned != null &&
    planned > 0 &&
    percentRaw != null;
  const status = showMeter ? hoursUsageStatus(planned, actual, traffic) : null;
  const ratioLabel = `${formatHours(actual)} / ${formatHours(planned)}`;
  const hasBreakdown = workshopHours != null || montageHours != null;

  return (
    <div className="eh-po-hours-usage">
      <div className={clsx("eh-po-hours-usage-hero", !display && "eh-po-hours-usage-hero--plain")}>
        {display ? (
          <div
            className={clsx(
              "eh-hours-usage-cell eh-po-hours-usage-ring",
              display.loading
                ? null
                : display.trafficLight === "over" || display.isOver
                  ? "eh-hours-usage-cell--over"
                  : display.trafficLight === "warn"
                    ? "eh-hours-usage-cell--warn"
                    : "eh-hours-usage-cell--ok",
            )}
            title={display.tooltip}
          >
            {display.loading ? (
              <span
                className="eh-hours-usage-ring eh-hours-usage-ring--loading"
                aria-hidden
              />
            ) : (
              <span
                className="eh-hours-usage-ring"
                style={{ "--eh-hours-p": display.ringPercent } as React.CSSProperties}
                aria-hidden
              >
                <span className="eh-hours-usage-label">{display.displayPercent}%</span>
              </span>
            )}
            <span className="sr-only">
              {display.loading
                ? display.tooltip
                : `${display.displayPercent}% genutzt (${ratioLabel})`}
            </span>
          </div>
        ) : null}

        <div className="eh-po-hours-usage-stats">
          <div className="eh-po-hours-usage-stat">
            <span className="eh-po-hours-usage-label">Ist-Stunden</span>
            <strong className="eh-po-hours-usage-value">
              {entry.loading ? "\u2026" : entry.error ? "\u2013" : formatHours(actual)}
            </strong>
          </div>
          <div className="eh-po-hours-usage-stat">
            <span className="eh-po-hours-usage-label">Kalk. Stunden</span>
            <strong className="eh-po-hours-usage-value">
              {planned != null && planned > 0 ? formatHours(planned) : "\u2013"}
            </strong>
          </div>
        </div>
      </div>

      {hasBreakdown ? (
        <ul className="eh-po-hours-usage-chips">
          {workshopHours != null ? (
            <li className="eh-po-hours-usage-chip">
              <span>Werkstatt</span>
              <strong>{formatHours(workshopHours)}</strong>
            </li>
          ) : null}
          {montageHours != null ? (
            <li className="eh-po-hours-usage-chip">
              <span>Montage</span>
              <strong>{formatHours(montageHours)}</strong>
            </li>
          ) : null}
        </ul>
      ) : null}

      {entry.loading ? (
        <p className="text-sm text-label-tertiary">Lade Stundenverbrauch {"\u2026"}</p>
      ) : null}

      {entry.error ? (
        <p className="text-sm text-status-red">Ist-Stunden konnten nicht geladen werden.</p>
      ) : null}

      {!entry.loading && !entry.error && planned != null && planned <= 0 ? (
        <p className="text-sm text-label-tertiary">Keine kalkulierten Stunden hinterlegt.</p>
      ) : null}

      {showMeter ? (
        <div className="eh-po-hours-usage-meter-block">
          <div className="eh-po-hours-usage-meter">
            <div
              className={clsx(
                "eh-po-hours-usage-track",
                traffic === "over" && "eh-po-hours-usage-track--over",
                traffic === "warn" && "eh-po-hours-usage-track--warn",
                traffic === "ok" && "eh-po-hours-usage-track--ok",
              )}
              role="progressbar"
              aria-valuenow={Math.max(0, percentRaw!)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${percentRaw}% der kalkulierten Stunden genutzt`}
            >
              <div
                className="eh-po-hours-usage-fill"
                style={{ width: `${Math.min(100, Math.max(0, percentRaw!))}%` }}
              />
            </div>
            <span
              className={clsx(
                "eh-po-hours-usage-meter-pct",
                traffic === "over" && "eh-po-hours-usage-meter-pct--over",
                traffic === "warn" && "eh-po-hours-usage-meter-pct--warn",
              )}
            >
              {percentRaw}%
            </span>
          </div>
          {status ? (
            <p
              className={clsx(
                "eh-po-hours-usage-status",
                `eh-po-hours-usage-status--${status.tone}`,
              )}
            >
              {status.text}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
